import { type FC, useState } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { IMPORTANCE_COLORS, CATEGORY_ICONS } from '@/services/economic-calendar';
import type { BondYield, CreditSpread, EconomicEvent } from '@/types';

// ---------------------------------------------------------------------------
// Economic Calendar + Bond Yields — static reference data (Mar 2026 snapshot)
// ---------------------------------------------------------------------------

type Tab = 'calendar' | 'bonds' | 'spreads';

function formatYield(y: number): string {
  return `${y.toFixed(3)}%`;
}

function formatChange(c: number): string {
  const sign = c >= 0 ? '+' : '';
  return `${sign}${c.toFixed(3)}%`;
}

const BondRow: FC<{ bond: BondYield }> = ({ bond }) => {
  const isRisky = bond.yield > 8;
  return (
    <div className="flex items-center justify-between py-1 border-b border-slate-800/40 last:border-0">
      <div className="flex items-center gap-2">
        <span className="text-[9px] font-mono text-slate-400 w-4">{bond.countryCode}</span>
        <span className="text-[9px] text-slate-500">{bond.maturity}</span>
        <span className="text-[10px] font-medium text-slate-300">{bond.country}</span>
      </div>
      <div className="flex items-center gap-2 tabular-nums">
        <span className={clsx('text-[11px] font-bold', isRisky ? 'text-red-400' : 'text-slate-100')}>
          {formatYield(bond.yield)}
        </span>
        <span className={clsx('text-[9px]', bond.change >= 0 ? 'text-red-400' : 'text-emerald-400')}>
          {formatChange(bond.change)}
        </span>
      </div>
    </div>
  );
};

const SpreadRow: FC<{ spread: CreditSpread }> = ({ spread }) => {
  const isElevated = spread.spread > 300;
  return (
    <div className="flex items-center justify-between py-1 border-b border-slate-800/40 last:border-0">
      <div>
        <div className="text-[10px] font-medium text-slate-300">{spread.name}</div>
        <div className="text-[9px] text-slate-600">{spread.description.slice(0, 42)}</div>
      </div>
      <div className="flex items-center gap-2 tabular-nums shrink-0 ml-2">
        <span className={clsx('text-[11px] font-bold', isElevated ? 'text-red-400' : 'text-slate-100')}>
          {spread.spread}bps
        </span>
        <span className={clsx('text-[9px]', spread.change >= 0 ? 'text-red-400' : 'text-emerald-400')}>
          {spread.change >= 0 ? '+' : ''}{spread.change}
        </span>
      </div>
    </div>
  );
};

const EventRow: FC<{ event: EconomicEvent }> = ({ event }) => {
  const isToday = event.date === new Date().toISOString().slice(0, 10);
  return (
    <div className={clsx(
      'flex items-start gap-2 py-1.5 border-b border-slate-800/30 last:border-0',
      isToday && 'bg-blue-950/20 rounded',
    )}>
      <div className="shrink-0 text-center w-10">
        <div className="text-[9px] text-slate-500">{event.date.slice(5)}</div>
        <div className="text-[9px] text-slate-600">{event.time}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-[10px]">{CATEGORY_ICONS[event.category] ?? '📌'}</span>
          <span className="text-[10px] font-medium text-slate-200 truncate">{event.title}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[9px] text-slate-500">{event.country}</span>
          {event.forecast && (
            <span className="text-[9px] text-blue-400">▸ {event.forecast}</span>
          )}
          {event.actual && (
            <span className="text-[9px] text-emerald-400 font-bold">✓ {event.actual}</span>
          )}
        </div>
      </div>
      <span className={clsx(
        'shrink-0 text-[8px] font-bold uppercase',
        IMPORTANCE_COLORS[event.importance],
      )}>
        {'●'.repeat(event.importance === 'high' ? 3 : event.importance === 'medium' ? 2 : 1)}
      </span>
    </div>
  );
};

export const EconomicCalendarPanel: FC = () => {
  const economicCalendar = useAppStore((s) => s.economicCalendar);
  const bondYields = useAppStore((s) => s.bondYields);
  const creditSpreads = useAppStore((s) => s.creditSpreads);
  const [tab, setTab] = useState<Tab>('calendar');

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'calendar', label: 'Calendar', icon: '📅' },
    { id: 'bonds',    label: 'Yields',   icon: '💹' },
    { id: 'spreads',  label: 'Spreads',  icon: '📊' },
  ];

  // 과거 이벤트는 upcoming 뷰에서 제외 — 전부 과거면 정적 목록을 REF 배지 아래 그대로 노출
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = economicCalendar.filter((e) => e.date >= today);
  const calendarEvents = upcoming.length > 0 ? upcoming : economicCalendar;
  const highImportance = calendarEvents.filter((e) => e.importance === 'high').length;
  // ForexFactory 라이브 데이터 여부 (id prefix로 판별)
  const isLive = economicCalendar.some((e) => e.id.startsWith('ec-live-'));

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-blue-400">Econ Calendar</span>
          {isLive ? (
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-950/60 text-emerald-400 border border-emerald-900/50 uppercase tracking-wide">
              LIVE · This Week
            </span>
          ) : (
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-slate-800/80 text-slate-400 border border-slate-700 uppercase tracking-wide">
              REF · Mar 2026
            </span>
          )}
          {tab === 'calendar' && highImportance > 0 && (
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-red-950/60 text-red-300 border border-red-900/40">
              {highImportance} HIGH
            </span>
          )}
        </div>
        <div className="flex gap-0.5">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={clsx(
                'px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors',
                tab === t.id
                  ? 'bg-blue-900/50 text-blue-300'
                  : 'text-slate-500 hover:text-slate-300',
              )}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 pb-2 scrollbar-thin">
        {tab === 'calendar' && upcoming.length === 0 && (
          <div className="text-[9px] text-slate-600 py-1.5 border-b border-slate-800/30">
            No upcoming events — showing static reference entries
          </div>
        )}
        {tab === 'calendar' && calendarEvents.map((ev) => (
          <EventRow key={ev.id} event={ev} />
        ))}
        {tab === 'bonds' && bondYields.map((b, i) => (
          <BondRow key={i} bond={b} />
        ))}
        {tab === 'spreads' && creditSpreads.map((s) => (
          <SpreadRow key={s.name} spread={s} />
        ))}
      </div>
    </div>
  );
};
